'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { BundleType, getDefaultTitle, type BundleDraft } from '@/components/products/bundle-config';
import { useBundlesStore } from '@/components/products/use-bundles-store';
import { ProductCard, type ProductCardBundle } from './ProductCard';

/**
 * The owner's own products grid. The storefront IS the editor since
 * 2026-08-21, so these are the same cards a buyer sees — each one carrying an
 * Edit pill, and the list ending in the way to make another.
 *
 * The cards render off the editing draft rather than the server's bundles, so
 * a price the owner types is on the card before it is on the server. That is
 * also why the store is the single source here: two copies of one catalogue is
 * how a storefront starts disagreeing with itself.
 */
export function OwnerProducts({
  bundles,
  currency,
  username,
}: {
  /** The owner's whole catalogue. A save replaces exactly this list. */
  bundles: BundleDraft[];
  currency: string;
  username: string;
}) {
  const initialize = useBundlesStore((s) => s.initialize);
  const drafts = useBundlesStore((s) => s.bundles);
  const removed = useBundlesStore((s) => s.removed);
  const undoRemove = useBundlesStore((s) => s.undoRemove);
  const add = useBundlesStore((s) => s.add);
  const editingId = useBundlesStore((s) => s.editingId);
  const busy = useBundlesStore((s) => s.editingId !== null || s.saving);
  const savedAt = useBundlesStore((s) => s.savedAt);
  const save = useBundlesStore((s) => s.save);
  // An open card shows its own failure under its Save; this is for Remove and Undo.
  const failed = useBundlesStore((s) => !!s.errors.general && !s.editingId);
  const [seeded, setSeeded] = useState(false);
  const router = useRouter();

  useEffect(() => {
    initialize(currency, username, bundles);
    setSeeded(true);
    // Seeded once from the server render. Later saves change the page's data
    // and every bundle's id, and re-seeding from that would swap the ids out
    // from under an open editor mid-keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A save has already revalidated the server cache; this pulls it into the
  // client's. Held back while an editor is open, so a Remove's refresh cannot
  // land under a card being typed in.
  useEffect(() => {
    if (savedAt && !editingId) router.refresh();
  }, [savedAt, editingId, router]);

  // The trial leads, as it does for a visitor, and is edited here like a pack
  // (2026-09-26) — the intro card's Edit opens the hours, since it is the calendar.
  const byTrialThenPrice = (a: { type: BundleType; price: number }, b: typeof a) =>
    a.type === b.type ? a.price - b.price : a.type === BundleType.TRIAL ? -1 : 1;
  const ordered = [...drafts].sort(byTrialThenPrice);

  // Before the store is seeded there is nothing in it, and rendering that would
  // blank a live storefront for a frame.
  const cards = (seeded ? ordered : [...bundles].sort(byTrialThenPrice)).map((draft) =>
    toCard(draft, currency)
  );

  return (
    <div className="flex flex-col gap-card">
      {cards.map((bundle) => (
        <ProductCard
          key={bundle.id}
          bundle={bundle}
          tutorUsername={username}
          loggedIn
          isOwner
          editable
        />
      ))}

      {removed.map((bundle) => (
        <div
          key={bundle.id}
          className="flex items-center justify-between gap-3 rounded-[var(--sf-radius)] border border-[var(--sf-ground-line)] px-4 py-3"
        >
          <span className="truncate text-p-3 font-semibold text-[var(--sf-ground-mut)]">
            {bundle.title || getDefaultTitle(bundle.type, bundle.numberOfClasses, bundle.duration)}{' '}
            — off your page
          </span>
          <button
            type="button"
            onClick={() => undoRemove(bundle.id)}
            disabled={busy}
            className="mm-btn shrink-0 text-p-3 font-bold text-[var(--sf-ground-fg)] underline disabled:opacity-35"
          >
            Undo
          </button>
        </div>
      ))}

      {/* Retry is theirs to press: a silent one would repeat a rejection
          forever, and the whole catalogue is at stake on every attempt. */}
      {failed && (
        <div className="flex items-center justify-between gap-3 rounded-[var(--sf-radius)] border border-error px-4 py-3">
          <span className="text-p-3 font-semibold text-[var(--sf-ground-fg)]">
            Not saved — your page still shows what it showed before.
          </span>
          <button
            type="button"
            onClick={() => void save()}
            className="mm-btn shrink-0 text-p-3 font-bold text-[var(--sf-ground-fg)] underline"
          >
            Try again
          </button>
        </div>
      )}

      {/* The intro is a product like the packages, so it is added with them. */}
      <div className="flex flex-col gap-card">
        {seeded && !drafts.some((b) => b.type === BundleType.TRIAL) && (
          <AddButton onClick={() => add(BundleType.TRIAL)} disabled={busy}>
            Add an intro call
          </AddButton>
        )}
        <AddButton onClick={() => add(BundleType.ONE_ON_ONE)} disabled={busy}>
          Add a package
        </AddButton>
      </div>
    </div>
  );
}

function AddButton({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="mm-btn sf-ghost min-h-11 flex-1 px-4 py-2.5 text-p-3 font-bold disabled:opacity-35"
    >
      {children}
    </button>
  );
}

export function toCard(draft: BundleDraft, currency: string): ProductCardBundle {
  return {
    id: draft.id,
    price: draft.price,
    previousPrice: draft.originalPrice,
    title: draft.title,
    numberOfClasses: draft.numberOfClasses,
    currency,
    type: draft.type,
    duration: draft.duration,
  };
}
